import React, { useEffect, useState } from "react";
import { FileText } from "lucide-react";
import { getBidderByUuid, getMultipleFilesByOwnerIdentifier } from "services";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../shadcn/components/ui/dialog";

interface JointBidderItem {
  email: string;
  bidder_uuid?: string;
  access_type?: string;
  name?: string;
}

interface JBDetailsModalProps {
  id: string | string[] | undefined;
  open: boolean;
  onClose: () => void;
}

const JBDetailsModal = ({ id, open, onClose }: JBDetailsModalProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<JointBidderItem[]>([]);
  const [documentUrls, setDocumentUrls] = useState<string[]>([]);

  useEffect(() => {
    const bidderUuid = Array.isArray(id) ? id[0] : id;
    if (!bidderUuid) return;

    const fetchBidder = async () => {
      setLoading(true);
      setError(null);
      try {
        const data: any = await getBidderByUuid(bidderUuid as string);
        const relations: any[] = data?.bidder_relations || [];
        const extracted: JointBidderItem[] = relations
          .map((rel: any) => ({
            email: rel?.meta?.email,
            bidder_uuid: rel?.bidder_uuid,
            access_type: rel?.type,
            name: rel?.meta?.name,
          }))
          .filter(
            (rel: JointBidderItem) =>
              typeof rel.email === "string" && rel.email.trim().length > 0
          );

        // Ensure Primary (if any) is first
        const isPrimary = (t?: string) =>
          (t || "").toLowerCase() === "primary" ||
          (t || "").toLowerCase() === "owner";
        const sorted = [...extracted].sort(
          (a, b) =>
            (isPrimary(b.access_type) ? 1 : 0) -
            (isPrimary(a.access_type) ? 1 : 0)
        );
        setItems(sorted);

        if (data) {
          const jointDeclarationData: any =
            await getMultipleFilesByOwnerIdentifier(
              typeof id === "string" ? id : "",
              "joint-declaration-documents"
            );

          if (jointDeclarationData) {
            const filteredJointDeclarationData = jointDeclarationData?.map(
              (item: any) => process.env.NEXT_PUBLIC_IMAGIX_URL + item?.uuid
            );
            setDocumentUrls(filteredJointDeclarationData);
          }
        }
      } catch (err: any) {
        setError("Failed to load bidder details.");
      } finally {
        setLoading(false);
      }
    };

    fetchBidder();
  }, [id]);

  const totalCount = items.length;

  const renderTypeBadge = (type?: string, customName?: string) => {
    const normalized = (type || "").toLowerCase();
    const isPrimary = normalized === "primary" || normalized === "owner";
    const label = isPrimary ? "Primary Bidder" : "Co-Bidder";
    const base =
      "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold";
    const primaryClasses = "bg-amber-100 text-amber-800 ring-1 ring-amber-200";
    const coBidderClasses = "bg-rose-100 text-rose-700 ring-1 ring-rose-200";
    return (
      <span
        className={`${base} ${isPrimary ? primaryClasses : coBidderClasses}`}
      >
        {customName ?? label}
      </span>
    );
  };

  const getInitial = (name?: string, email?: string) => {
    const source = (name && name.trim().length > 0 ? name : email) || "";
    return source.trim().charAt(0).toUpperCase() || "?";
  };

  const downloadFile = async (url: string, index: number) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();

      // guess extension from MIME type
      let extension = "";
      switch (blob.type) {
        case "application/pdf":
          extension = "pdf";
          break;
        case "image/jpeg":
          extension = "jpg";
          break;
        case "image/png":
          extension = "png";
          break;
        default:
          extension = "bin"; // fallback
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `document-${index + 1}.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  const handleDownloadAll = () => {
    documentUrls.forEach((url, index) => downloadFile(url, index));
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent className="p-0 sm:max-w-4xl sm:rounded-2xl">
        <DialogTitle></DialogTitle>
        <div className="p-6 lg:px-8">
          <DialogHeader>
            <div className="flex flex-col items-start justify-between mb-4">
              <div className="flex justify-between w-full items-center">
                <h3 className="font-openSans text-red-900 text-xl md:text-2xl font-extrabold leading-8">
                  Joint Bidder Application
                </h3>
                <button
                  type="button"
                  disabled={documentUrls?.length === 0}
                  onClick={handleDownloadAll}
                  className={`inline-flex mr-5   items-center rounded-xl border border-amber-400 bg-orange-100 px-4 py-3 text-sm font-bold text-amber-900   ${
                    documentUrls?.length === 0
                      ? "opacity-50 hover:cursor-not-allowed"
                      : "hover:bg-orange-200"
                  }`}
                >
                  <FileText className="h-8 w-8 " />
                  Joint Bidder Declaration
                </button>
              </div>
              <div className="mt-4 lg:mt-0 max-w-[550px]">
                <p className=" font-openSans text-web-body-md leading-6 text-brand-textGrey">
                  This application is submitted by multiple bidders acting
                  jointly. Review all co-bidders below.{" "}
                  <span className="font-semibold">Primary Bidder</span> is
                  listed first.
                </p>
                <p className="mt-2 font-openSans text-web-body-md leading-6 text-brand-textGrey">
                  Total bidders in this application:{" "}
                  <span className="font-semibold">{totalCount}</span>
                </p>
              </div>
            </div>
          </DialogHeader>

          {loading ? (
            <div className="p-6 text-sm text-gray-600">Loading...</div>
          ) : error ? (
            <div className="p-6 text-sm text-red-600">{error}</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">
              No joint bidders found.
            </div>
          ) : (
            <div className="mt-2 max-h-[260px] overflow-y-auto min-w-[250px]  rounded-t-xl rounded-b-xl border">
              <div className="grid grid-cols-12 items-center  bg-gray-50 px-4 py-3 text-left">
                <div className="col-span-1 text-xs font-semibold text-gray-600">
                  S NO
                </div>
                <div className="col-span-4 text-xs font-semibold text-gray-600">
                  BIDDER NAME
                </div>
                <div className="col-span-5 text-xs font-semibold text-gray-600">
                  EMAIL ADDRESS
                </div>
                <div className="col-span-2 text-xs font-semibold text-gray-600">
                  TYPE OF BIDDER
                </div>
              </div>

              <div className="max-h-80 overflow-auto border">
                <ul role="list" className="divide-y">
                  {items.map((item, idx) => {
                    const initial = getInitial(item.name, item.email);
                    return (
                      <li
                        key={`${item.email}-${idx}`}
                        className="grid grid-cols-12 items-center px-4 py-4 gap-x-1 hover:bg-gray-50"
                      >
                        <div className="col-span-1 text-sm font-semibold text-brand-bgBlack">
                          {idx + 1}
                        </div>
                        <div className="col-span-4 flex items-center gap-3">
                          <div className="hidden sm:flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 text-sm font-semibold">
                            {initial}
                          </div>
                          <div className="min-w-0">
                            <div className=" font-openSans text-sm font-bold text-brand-bgBlack">
                              {item.name || "—"}
                            </div>
                          </div>
                        </div>
                        <div className="col-span-5">
                          {
                            <p className="font-openSans truncate  text-sm ">
                              {item.email}
                            </p>
                          }
                        </div>
                        <div className="col-span-2">
                          {renderTypeBadge(item.access_type)}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}

          <div className="mt-6  px-4 py-3 text-sm text-brand-textGrey">
            <span className="font-semibold">Tip:</span> Use the scroll area to
            view all co-bidders. Only the{" "}
            <span className="font-semibold">Primary Bidder</span> can place and
            confirm bids. Co-Bidders have viewing rights and receive
            notifications.
          </div>

          <div className="mt-4 flex w-full justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center rounded-xl border border-amber-400 bg-orange-100 px-3 py-2.5 text-sm font-semibold text-amber-900 hover:bg-orange-200"
            >
              Close
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default JBDetailsModal;
